import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import ImageLightbox from './ImageLightbox';

// Inline-style properties authored content may keep. Everything else — fonts,
// sizes, line-heights, margins, CSS custom properties — is the look of
// wherever the text was copied from rather than the author's intent, and
// rendering it lets a source page paint its own styling onto ours. Emphasis
// stays because Google Docs writes bold as `font-weight: 700` on a <span>
// instead of <strong>, so dropping it would flatten every pasted document.
export const KEEP_STYLE_PROPS = new Set([
  'font-weight', 'font-style', 'text-decoration', 'text-decoration-line', 'text-align',
  'background-color', // the toolbar's highlight button — see isPastedBackground
]);

function parseColor(value) {
  const hex = value.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  }
  const rgb = value.match(/^rgba?\(([^)]+)\)$/);
  if (rgb) {
    const parts = rgb[1].split(',').map((p) => parseFloat(p));
    if (parts.length >= 3 && parts.slice(0, 3).every(Number.isFinite)) return parts.slice(0, 3);
  }
  if (value === 'white') return [255, 255, 255];
  return null;
}

// A deliberate highlight is a saturated colour (the toolbar defaults to
// #FFF3C4). A near-white one is the page colour of wherever the text was
// copied from, which is what renders as a stray box on our cream pages.
function isPastedBackground(raw) {
  const value = String(raw || '').trim().toLowerCase();
  if (!value) return true;
  if (value === 'transparent' || value.startsWith('var(')) return true;
  if (['initial', 'inherit', 'unset', 'revert', 'none'].includes(value)) return true;
  const rgb = parseColor(value);
  if (!rgb) return false; // a named colour we don't parse — assume it was meant
  return rgb.every((channel) => channel >= 240);
}

// Keeps only the allowlisted declarations from one style attribute. Shared
// with RichTextEditor's paste handler so what survives a paste and what
// survives a render are the same set.
export function filterInlineStyle(value) {
  return String(value || '')
    .split(';')
    .map((d) => d.trim())
    .filter(Boolean)
    .filter((declaration) => {
      const colon = declaration.indexOf(':');
      if (colon < 0) return false;
      const prop = declaration.slice(0, colon).trim().toLowerCase();
      if (!KEEP_STYLE_PROPS.has(prop)) return false;
      if (prop === 'background-color') return !isPastedBackground(declaration.slice(colon + 1));
      return true;
    })
    .join('; ');
}

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Anchors created by the editor never carry target/rel — force safe new-tab
  // behavior here so every render path (builder preview, student view) agrees.
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
  // Content saved before the editor filtered pastes still carries the source
  // page's styling, so constrain it here on the way out as well.
  if (node.hasAttribute?.('style')) {
    const kept = filterInlineStyle(node.getAttribute('style'));
    if (kept) node.setAttribute('style', kept);
    else node.removeAttribute('style');
  }
});

const ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'span', 'div',
  'ul', 'ol', 'li', 'a', 'h1', 'h2', 'h3', 'blockquote', 'font', 'img',
];
const ALLOWED_ATTR = ['href', 'style', 'color', 'size', 'face', 'src', 'alt'];

export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR });
}

// True when the HTML has no visible content — contentEditable leaves behind
// stray `<br>`/`<p></p>` wrappers once a user types then deletes everything.
export function isRichTextEmpty(html) {
  if (!html || typeof html !== 'string') return true;
  return toPlainText(html).length === 0;
}

// Strip tags for contexts that render plain text (card snippets, drafts
// list) — those use plain JSX text nodes, not dangerouslySetInnerHTML, so
// leftover markup would otherwise show up as literal "<p>" characters.
// Guards non-string input too — a field can end up holding stale non-string
// data (e.g. an array left over from before its type was changed in the
// builder), and this is shared by enough callers that it needs to be inert
// rather than throwing.
export function toPlainText(html) {
  if (!html || typeof html !== 'string') return '';
  // Tags first, so block boundaries still become spaces rather than running
  // two paragraphs together. Then DOMParser decodes the entities: the old
  // chain special-cased &nbsp; and nothing else, so a headline reading
  // "Motion Designer & Illustrator" was stored as "&amp;" and rendered with
  // the entity showing. Parsing as text/html never executes scripts.
  const stripped = html.replace(/<[^>]*>/g, ' ');
  const decoded = new DOMParser().parseFromString(stripped, 'text/html').body.textContent || '';
  return decoded.replace(/\s+/g, ' ').trim();
}

// Read-only render of instructor-authored rich text (course overview, module
// summaries, page/discussion/overview bodies). Always sanitize here too —
// content may have been saved before the editor existed, or via the API.
export default function RichText({ html, className = '', emptyText }) {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxAlt, setLightboxAlt] = useState('');
  const clean = sanitizeHtml(html);
  const bodyRef = useRef(null);
  // Authored <img>s are click-to-zoom; make them reachable by keyboard too.
  useEffect(() => {
    bodyRef.current?.querySelectorAll('img').forEach((img) => { img.tabIndex = 0; });
  }, [clean]);
  if (isRichTextEmpty(clean)) {
    return emptyText ? <p className={`text-press-muted ${className}`}>{emptyText}</p> : null;
  }
  // Event delegation — dangerouslySetInnerHTML content has no React handlers
  // of its own, so a click anywhere in the block is checked for an <img>.
  const handleClick = (e) => {
    if (e.target.tagName === 'IMG' && (e.type === 'click' || e.key === 'Enter' || e.key === ' ')) {
      if (e.key) e.preventDefault(); // Space would scroll the page
      setLightboxSrc(e.target.getAttribute('src'));
      setLightboxAlt(e.target.getAttribute('alt') || 'Expanded image');
    }
  };
  return (
    <>
      <div ref={bodyRef} className={`rich-text-content ${className}`} onClick={handleClick} onKeyDown={handleClick} dangerouslySetInnerHTML={{ __html: clean }} />
      <ImageLightbox src={lightboxSrc} alt={lightboxAlt} onClose={() => setLightboxSrc(null)} />
    </>
  );
}
